// src/restaurant/pages/OrderDetails.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { GoogleMap, Marker, DirectionsRenderer, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { watchOrderStatusUpdates } from '../../utils/orderStatusSync';
import { toast } from 'react-toastify';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import DriverTracker from '../../components/DriverTracker';
import './OrderDetails.css';

// مكون للخريطة الاحتياطية عند فشل تحميل خرائط جوجل
const FallbackMap = ({ address, error }) => {
  return (
    <div className="fallback-map">
      <div className="fallback-map-icon">🗺️</div>
      <div className="fallback-map-message">
        <h3>تعذر تحميل الخريطة</h3>
        {error && <p className="fallback-map-error">{error.message || 'حدث خطأ أثناء تحميل الخريطة'}</p>}
        {address && (
          <div className="fallback-map-address">
            <strong>العنوان:</strong> {address}
          </div>
        )}
      </div>
    </div>
  );
};

const OrderDetails = () => {
  const { id } = useParams();
  
  // استخدام المفتاح من وحدة التكوين المحسنة
  const mapsApiKey = window.ENV_CONFIG ? window.ENV_CONFIG.getGoogleMapsKey() : process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: mapsApiKey,
    onError: (error) => {
      console.error('خطأ في تحميل Google Maps API:', error);
      setMapError(error);
    }
  });
  
  const { t, language } = useLanguage();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directions, setDirections] = useState(null);
  const [error, setError] = useState('');
  const [mapError, setMapError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showDriverTracker, setShowDriverTracker] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const updateDirections = useCallback((orderData) => {
    if (!isLoaded || !window?.google) {
      console.warn('الخريطة لم تجهز أو مكتبة Google Maps غير متاحة.');
      return;
    }

    const origin = parseLocation(orderData.restaurantLocation);
    const destination = parseLocation(orderData.customerLocation);

    if (!origin || !destination) {
      console.error('مواقع غير صالحة للحصول على الاتجاهات:', { origin, destination });
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error('خطأ في جلب الاتجاهات:', status);
          // محاولة مرة أخرى في حالة الفشل بفاصل زمني
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prevCount => prevCount + 1);
              updateDirections(orderData);
            }, 2000);
          }
        }
      }
    );
  }, [isLoaded, retryCount]);

  useEffect(() => {
    if (!id) return;

    let orderUnsubscribe = () => {};
    let statusUpdateUnsubscribe = () => {};
    let driverLocationUnsubscribe = () => {};

    const setupOrderListener = async () => {
      try {
        const orderRef = doc(db, 'orders', id);

        orderUnsubscribe = onSnapshot(orderRef, (orderDoc) => {
          if (!orderDoc.exists()) {
            setError(t('order_not_found'));
            setLoading(false);
            return;
          }

          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          setOrder(orderData);

          // تحديث موقع السائق من بيانات الطلب
          if (orderData.driverLocation) {
            const location = parseLocation(orderData.driverLocation);
            if (location) {
              setDriverLocation(location);
              console.log('تم تحديث موقع السائق:', location);
            }
          }

          setLoading(false);

          if (isLoaded && orderData.restaurantLocation && orderData.customerLocation) {
            updateDirections(orderData);
          }
        }, (err) => {
          console.error('خطأ في مراقبة تفاصيل الطلب:', err);
          setError(t('error_loading_order'));
          setLoading(false);
        });

        // مراقب منفصل لتحديثات موقع السائق
        if (order?.driverId) {
          const driverRef = doc(db, 'drivers', order.driverId);
          driverLocationUnsubscribe = onSnapshot(driverRef, (driverDoc) => {
            if (driverDoc.exists()) {
              const driverData = driverDoc.data();
              if (driverData.currentLocation) {
                const location = parseLocation(driverData.currentLocation);
                if (location) {
                  setDriverLocation(location);
                  console.log('تم تحديث موقع السائق في الوقت الفعلي:', location);
                }
              }
            }
          }, (err) => {
            console.error('خطأ في مراقبة موقع السائق:', err);
          });
        }

        // استخدام المراقب لتحديثات حالة الطلب
        if (id) {
          statusUpdateUnsubscribe = watchOrderStatusUpdates(id, (newStatus) => {
            console.log(`تحديث حالة الطلب: ${newStatus}`);
            // تحديث حالة الطلب محليًا
            setOrder(prevOrder => prevOrder ? { ...prevOrder, status: newStatus } : null);
            
            toast.info(
              language === 'ar'
                ? `تم تحديث حالة الطلب إلى: ${newStatus}`
                : `Order status updated to: ${newStatus}`
            );
          });
        }

      } catch (error) {
        console.error('خطأ في جلب تفاصيل الطلب:', error);
        setError(t('error_loading_order'));
        setLoading(false);
      }
    };

    setupOrderListener();

    return () => {
      try {
        orderUnsubscribe();
        statusUpdateUnsubscribe();
        driverLocationUnsubscribe();
      } catch (cleanupError) {
        console.error('خطأ أثناء تنظيف المراقبين:', cleanupError);
      }
    };
  }, [id, isLoaded, t, language, updateDirections, order?.driverId]);

  const parseLocation = (location) => {
    if (!location) return null;

    try {
      if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
        return { lat: location.latitude, lng: location.longitude };
      }

      if (typeof location.lat !== 'undefined' && typeof location.lng !== 'undefined') {
        return {
          lat: typeof location.lat === 'function' ? location.lat() : Number(location.lat),
          lng: typeof location.lng === 'function' ? location.lng() : Number(location.lng)
        };
      }

      if (Array.isArray(location) && location.length === 2) {
        return { lat: location[1], lng: location[0] };
      }

      console.warn('تنسيق موقع غير معروف:', location);
      return null;
    } catch (error) {
      console.error('خطأ في تحليل الموقع:', error);
      return null;
    }
  };

  const cancelOrder = async () => {
    if (!order) return;

    if (window.confirm(t('confirm_cancel_order'))) {
      try {
        await updateDoc(doc(db, 'orders', id), { status: 'ملغي ❌' });
        setSuccess(t('order_cancelled_success'));
        // تحديث الطلب محليًا بعد الإلغاء
        setOrder(prevOrder => ({ ...prevOrder, status: 'ملغي ❌' }));
      } catch (error) {
        console.error('خطأ في إلغاء الطلب:', error);
        setError(t('error_cancelling_order'));
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return t('not_available');

    const date = timestamp.toDate();
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleDriverTracker = () => {
    setShowDriverTracker((prev) => !prev);
  };

  const handleMarkerClick = (type) => {
    setSelectedMarker(type);
  };

  const handleCloseInfoWindow = () => {
    setSelectedMarker(null);
  };

  const retryLoadMap = () => {
    setMapError(null);
    setRetryCount(0);
    // محاولة إعادة تحميل الخريطة
    if (window.ENV_CONFIG && typeof window.ENV_CONFIG.validateGoogleMapsKey === 'function') {
      window.ENV_CONFIG.validateGoogleMapsKey((success) => {
        if (success && order) {
          updateDirections(order);
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="restaurant-loading">
        <div className="restaurant-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="restaurant-order-details-page">
      <RestaurantSidebar />
      <div className="restaurant-order-details-content">
        <RestaurantHeader restaurantName={localStorage.getItem('restaurantName') || t('restaurant')} />
        
        <div className="order-details-container">
          <div className="order-details-header">
            <div>
              <h1>{t('order_details')} #{order?.orderCode || id.substring(0, 6)}</h1>
              {order?.createdAt && (
                <p className="order-date">{t('order_date')}: {formatTimestamp(order.createdAt)}</p>
              )}
            </div>
            
            <div className="order-status-badge" data-status={order?.status}>
              {order?.status}
            </div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          {order && driverLocation && (
            <div className="driver-tracker-controls">
              <button 
                className={`track-driver-btn ${showDriverTracker ? 'active' : ''}`} 
                onClick={toggleDriverTracker}
              >
                {showDriverTracker ? t('hide_driver_tracking') : t('track_driver')}
              </button>
            </div>
          )}
          
          {order && showDriverTracker && (
            <div className="driver-tracker-container">
              <DriverTracker 
                orderId={id} 
                driverId={order.driverId}
                initialDriverLocation={driverLocation}
              />
            </div>
          )}
          
          <div className="order-details-sections">
            <div className="order-details-column">
              <div className="order-details-section">
                <h2>{t('customer_info')}</h2>
                <div className="details-group">
                  <div className="detail-item">
                    <span className="detail-label">{t('name')}:</span>
                    <span className="detail-value">{order?.customerName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('phone')}:</span>
                    <span className="detail-value">{order?.customerPhone}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('address')}:</span>
                    <span className="detail-value">{order?.customerAddress}</span>
                  </div>
                </div>
              </div>
              
              <div className="order-details-section">
                <h2>{t('order_items')}</h2>
                <div className="order-items-list">
                  {order?.items?.length > 0 ? (
                    order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="order-item-details">
                          <span className="order-item-name">{item.name}</span>
                          <span className="order-item-price">
                            {item.price} {t('currency')} × {item.quantity}
                          </span>
                        </div>
                        <div className="order-item-total">{(item.price * item.quantity).toFixed(2)} {t('currency')}</div>
                      </div>
                    ))
                  ) : (
                    <p className="no-items-message">{t('no_items')}</p>
                  )}
                </div>
                
      {/* Calculate totals */}
      {(() => {
        const subtotal = order?.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
        const deliveryFee = typeof order?.deliveryFee === 'number' ? order.deliveryFee : 0;
        const discount = typeof order?.discount === 'number' ? order.discount : 0;
        const total = subtotal + deliveryFee - discount;
        return (
          <div className="order-totals">
            <div className="order-total-row">
              <span>{t('subtotal')}:</span>
              <span>{subtotal.toFixed(3)} {t('currency')}</span>
            </div>
            <div className="order-total-row">
              <span>{t('delivery_fee')}:</span>
              <span>{deliveryFee.toFixed(3)} {t('currency')}</span>
            </div>
            {discount > 0 && (
              <div className="order-total-row discount">
                <span>{t('discount')}:</span>
                <span>-{discount.toFixed(3)} {t('currency')}</span>
              </div>
            )}
            <div className="order-total-row grand-total">
              <span>{t('total')}:</span>
              <span>{total.toFixed(3)} {t('currency')}</span>
            </div>
          </div>
        );
      })()}
              </div>
              
              {(order?.status === 'جديد' || order?.status === 'قيد المعالجة') && (
                <div className="order-actions">
                  <button onClick={cancelOrder} className="cancel-order-btn">
                    {t('cancel_order')}
                  </button>
                </div>
              )}
            </div>
            
            <div className="order-details-column map-column">
              <div className="order-details-section">
                <h2>{t('delivery_route')}</h2>
                
                {loadError || mapError ? (
                  <div className="map-error-container">
                    <FallbackMap 
                      error={loadError || mapError} 
                      address={order?.customerAddress}
                    />
                    <button className="retry-map-btn" onClick={retryLoadMap}>
                      {t('retry_loading_map')}
                    </button>
                  </div>
                ) : !isLoaded ? (
                  <div className="map-loading">
                    <div className="map-spinner"></div>
                    <p>{t('loading_map')}</p>
                  </div>
                ) : (
                  <div className="map-container">
                    <GoogleMap
                      mapContainerClassName="order-map"
                      center={driverLocation || parseLocation(order?.restaurantLocation) || { lat: 24.774265, lng: 46.738586 }}
                      zoom={13}
                      options={{
                        fullscreenControl: true,
                        streetViewControl: false,
                        mapTypeControl: false
                      }}
                    >
                      {directions && (
                        <DirectionsRenderer
                          directions={directions}
                          options={{
                            suppressMarkers: true,
                            polylineOptions: {
                              strokeColor: '#4285F4',
                              strokeWeight: 5
                            }
                          }}
                        />
                      )}
                      
                      {order?.restaurantLocation && (
                        <Marker
                          position={parseLocation(order.restaurantLocation)}
                          icon={{
                            url: '/images/restaurant-marker.png',
                            scaledSize: new window.google.maps.Size(40, 40)
                          }}
                          onClick={() => handleMarkerClick('restaurant')}
                        />
                      )}
                      
                      {order?.customerLocation && (
                        <Marker
                          position={parseLocation(order.customerLocation)}
                          icon={{
                            url: '/images/customer-marker.png',
                            scaledSize: new window.google.maps.Size(40, 40)
                          }}
                          onClick={() => handleMarkerClick('customer')}
                        />
                      )}
                      
                      {driverLocation && (
                        <Marker
                          position={driverLocation}
                          icon={{
                            url: '/images/driver-marker.png',
                            scaledSize: new window.google.maps.Size(50, 50)
                          }}
                          onClick={() => handleMarkerClick('driver')}
                        />
                      )}
                      
                      {selectedMarker === 'restaurant' && order?.restaurantLocation && (
                        <InfoWindow
                          position={parseLocation(order.restaurantLocation)}
                          onCloseClick={handleCloseInfoWindow}
                        >
                          <div className="info-window">
                            <h3>{order.restaurantName}</h3>
                            <p>{order.restaurantAddress}</p>
                          </div>
                        </InfoWindow>
                      )}
                      
                      {selectedMarker === 'customer' && order?.customerLocation && (
                        <InfoWindow
                          position={parseLocation(order.customerLocation)}
                          onCloseClick={handleCloseInfoWindow}
                        >
                          <div className="info-window">
                            <h3>{order.customerName}</h3>
                            <p>{order.customerAddress}</p>
                            <p>{order.customerPhone}</p>
                          </div>
                        </InfoWindow>
                      )}
                      
                      {selectedMarker === 'driver' && driverLocation && (
                        <InfoWindow
                          position={driverLocation}
                          onCloseClick={handleCloseInfoWindow}
                        >
                          <div className="info-window">
                            <h3>{order.driverName}</h3>
                            <p>{t('current_location')}</p>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </div>
                )}
              </div>
              
              <div className="order-details-section">
                <h2>{t('delivery_info')}</h2>
                <div className="details-group">
                  <div className="detail-item">
                    <span className="detail-label">{t('driver')}:</span>
                    <span className="detail-value">{order?.driverName || t('not_assigned')}</span>
                  </div>
                  {order?.driverPhone && (
                    <div className="detail-item">
                      <span className="detail-label">{t('driver_phone')}:</span>
                      <span className="detail-value">{order.driverPhone}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">{t('payment_method')}:</span>
                    <span className="detail-value">{order?.paymentMethod || t('cash')}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('payment_status')}:</span>
                    <span className={`detail-value payment-status ${order?.isPaid ? 'paid' : 'unpaid'}`}>
                      {order?.isPaid ? t('paid') : t('unpaid')}
                    </span>
                  </div>
                  {order?.pickupTime && (
                    <div className="detail-item">
                      <span className="detail-label">{t('pickup_time')}:</span>
                      <span className="detail-value">{formatTimestamp(order.pickupTime)}</span>
                    </div>
                  )}
                  {order?.deliveryTime && (
                    <div className="detail-item">
                      <span className="detail-label">{t('delivery_time')}:</span>
                      <span className="detail-value">{formatTimestamp(order.deliveryTime)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;